// src/pages/MPFlow/phases/Phase2Creation.jsx
import React, { useState, useEffect } from 'react';
import { CpuChipIcon } from '@heroicons/react/24/outline';
import EdyMascot from '../components/EdyMascot';
import apiClient from '../../../lib/apiClient';
import CreativeCanvasEngine from '../../../engine/CreativeCanvasEngine/CreativeCanvasEngine';

export default function Phase2Creation({ profile, onNext, onBack }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [copy, setCopy]     = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [structure, setStructure] = useState(null);
  const [creativeId, setCreativeId] = useState('');
  const [tom, setTom]       = useState('profissional');

  const generate = async (selectedTom = tom) => {
    setLoading(true);
    setError('');
    try {
      // 1. Gera copy via LLM
      const copyRes = await apiClient.request('/mp/generate', {
        method: 'POST',
        body: JSON.stringify({
          nicho:       profile.nicho,
          diferencial: profile.diferencial,
          dor_cliente: profile.dor_cliente,
          tom:         selectedTom,
        }),
      });
      const generatedCopy = copyRes?.copy || null;

      // 2. Gera imagem via DALL-E (OpenAI gpt-image-1)
      // Usa sugestao_visual do LLM como prompt principal (já otimizado e detalhado)
      const imagePrompt = generatedCopy?.sugestao_visual
        || (profile.nicho + ' ' + (generatedCopy?.headline || ''));
      const creativeRes = await apiClient.request('/creative/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt:        imagePrompt,
          creative_type: 'image',
          business_type: profile.nicho,
        }),
      });
      const creativeStructure = creativeRes?.creative?.structure || creativeRes?.creative || null;
      const cId = creativeRes?.creative?.id || '';
      const img = creativeStructure?.image_url || '';

      setCopy(generatedCopy);
      setImageUrl(img);
      setStructure(creativeStructure);
      setCreativeId(cId);
      setTom(selectedTom);
    } catch {
      setError('Erro ao gerar criativo. Verifique sua conexão.');
    } finally {
      setLoading(false);
    }
  };

  // Gera ao montar
  useEffect(() => { generate('profissional'); }, []); // eslint-disable-line

  const handleNext = () => {
    if (!copy) return;
    onNext({ copy, imageUrl, structure, tom, creativeId });
  };

  if (loading) {
    return (
      <div className="mp-loading">
        <div className="mp-loading__icon"><CpuChipIcon className="icon-xl edy-processing" /></div>
        <div className="mp-loading__dots"><span /><span /><span /></div>
        <p className="mp-loading__msg">Gerando seu criativo com IA...</p>
      </div>
    );
  }

  return (
    <div>
      <EdyMascot phase="criacao" />

      {error && <div className="mp-alert mp-alert--error">{error}</div>}

      {copy && (
        <div className="mp-card">
          <h2 className="mp-phase-title">Seu Criativo IA</h2>
          <p className="mp-phase-sub">Copy gerado pela IA com base no seu perfil.</p>

          {/* Preview do criativo via CreativeCanvasEngine */}
          {structure?.canvas_data ? (
            <div className="mp-creative-preview">
              <CreativeCanvasEngine structure={structure} scale={0.26} />
            </div>
          ) : structure?.image_url ? (
            <div className="mp-creative-preview">
              <img className="mp-preview-image" src={structure.image_url} alt="Criativo" />
            </div>
          ) : imageUrl ? (
            <div className="mp-creative-preview">
              <img className="mp-preview-image" src={imageUrl} alt="Criativo" />
            </div>
          ) : null}

          {/* Copy gerado */}
          <div className="mp-copy-block">
            <div className="mp-copy-block__headline">"{copy.headline}"</div>
            <div className="mp-copy-block__legenda">{copy.legenda}</div>
            {copy.sugestao_visual && (
              <div className="mp-copy-block__visual-hint">
                Visual: {copy.sugestao_visual}
              </div>
            )}
            {(copy.publico_alvo || copy.publico_sugerido) && (
              <div className="mp-copy-block__audience">
                Publico: {(copy.publico_alvo || copy.publico_sugerido).idade} anos ·{' '}
                {((copy.publico_alvo || copy.publico_sugerido).interesses || []).join(', ')}
              </div>
            )}
          </div>

          <div className="mp-btn-row mp-mt-5">
            {onBack && (
              <button className="mp-btn mp-btn--ghost mp-btn--sm" onClick={onBack}>
                ← Voltar
              </button>
            )}
            <button className="mp-btn mp-btn--ghost mp-btn--sm" onClick={() => generate(tom)}>
              ↺ Regerar
            </button>
            <button className="mp-btn mp-btn--primary" onClick={handleNext}>
              Próximo: Refinar →
            </button>
          </div>
        </div>
      )}

      {!copy && !loading && !error && (
        <button className="mp-btn mp-btn--cyan" onClick={() => generate(tom)}>
          Gerar Criativo com IA
        </button>
      )}
    </div>
  );
}
